import JSZip from "jszip";
import { saveAs } from "file-saver";
import React, { useEffect, useRef, useState } from "react";
import "./AssetsManager.css";
import { Delete, Download, Upload } from "@mui/icons-material";

function SingleAssetDisplay(props: {
  name: string;
  url: string;
  eraseAsset: (assetUrl: string, assetName: string) => void;
  downloadAsset: (assetUrl: string, assetName: string) => void;
  img?: React.ReactNode;
  uploadAsset: (newAsset: File) => void;
}) {
  const [showButtons, setShowButtons] = useState(false);
  const inputFile = useRef(null);

  function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
    const files = event.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      const fileExtension = file.name.split(".").pop();
      const renamedFile = new File([file], props.name + "." + fileExtension, { type: file.type });
      props.uploadAsset(renamedFile);
    }
  }

  return (
    <div className="deck-asset" onMouseEnter={() => setShowButtons(true)} onMouseLeave={() => setShowButtons(false)}>
      <input type="file" ref={inputFile} style={{ display: "none" }} onChange={handleChange} />
      <button
        className="assets-manager-circular-button erase-button"
        onClick={() => props.eraseAsset(props.url, props.name)}
        style={{ display: showButtons ? "flex" : "none" }}
      >
        <Delete />
      </button>
      <button
        className="assets-manager-circular-button download-button"
        onClick={() => props.downloadAsset(props.url, props.name)}
        style={{ display: showButtons ? "flex" : "none" }}
      >
        <Download />
      </button>
      <button
        className="assets-manager-circular-button upload-button"
        onClick={() => (inputFile.current as unknown as HTMLInputElement).click()}
        style={{ display: showButtons ? "flex" : "none" }}
      >
        <Upload />
      </button>
      {props.img}
      <label>{props.name}</label>
    </div>
  );
}

export function AssetsManager(props: {
  projectSlug: string;
  deckName?: string;
  targetId?: string;
  assets: { [key: string]: { url: string } };
  updateDeckAssets?: (deckName: string, assets: any) => void;
  assetDeleted: (targetId: string, assetName: string) => void;
  updateGlobalAssets?: (assets: any) => void;
}) {
  const [assetStatus, setAssetStatus] = useState("");
  const [compactAssetsView, setCompactAssetsView] = useState(true);
  const [showCompactViewControl, setShowCompactViewControl] = useState(false);

  const assetDumpRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (assetDumpRef.current) {
      if (assetDumpRef.current.scrollHeight > assetDumpRef.current.clientHeight) {
        setShowCompactViewControl(true);
      }
    }
  }, [props.assets]);

  let assetList = [];
  for (let name of Object.keys(props.assets ?? {}).sort()) {
    let img;
    let url = props.assets[name].url;
    if (url?.includes(".ttf") || url?.includes(".otf")) {
      img = <img src={"/assets/icons/font-icon.png"} width={100} height={100}></img>;
      img = (
        <div className="font-preview" style={{ fontFamily: name }}>
          <span>Aa0!&</span>
        </div>
      );
    } else {
      img = <img src={url} width={100} height={100}></img>;
    }
    assetList.push(
      <SingleAssetDisplay
        key={name}
        name={name}
        url={url}
        eraseAsset={eraseAsset}
        downloadAsset={downloadAsset}
        img={img}
        uploadAsset={updateSingleAsset}
      />,
    );
  }

  function updateAssets(evt: React.FormEvent<HTMLFormElement>) {
    evt.preventDefault();
    setAssetStatus("Loading assets...");
    let formData = new FormData(evt.target as HTMLFormElement);
    if (props.targetId) {
      fetch("/api/update-deck-assets/" + props.projectSlug, {
        method: "POST",
        body: formData,
      })
        .then((response) => {
          if (response.ok) {
            setAssetStatus("assets updated");
          }
          return response.json();
        })
        .then((data) => {
          console.log(data);
          if (props.updateDeckAssets && props.deckName) {
            props.updateDeckAssets(props.deckName, data);
          }
        });
    } else {
      fetch("/api/update-global-assets/" + props.projectSlug, {
        method: "POST",
        body: formData,
      })
        .then((response) => {
          if (response.ok) {
            setAssetStatus("assets updated");
          }
          return response.json();
        })
        .then((data) => {
          console.log(data);
          if (props.updateGlobalAssets) {
            props.updateGlobalAssets(data);
          }
        });
    }
  }

  function updateSingleAsset(newAsset: File) {
    let data = new FormData();
    data.append("additional-assets", newAsset);
    props.targetId ? data.append("targetId", props.targetId) : null;
    if (props.targetId) {
      fetch("/api/update-deck-assets/" + props.projectSlug, {
        method: "POST",
        body: data,
      })
        .then((response) => {
          return response.json();
        })
        .then((data) => {
          console.log(data);
          if (props.updateDeckAssets && props.deckName) {
            props.updateDeckAssets(props.deckName, data);
          }
        });
    } else {
      fetch("/api/update-global-assets/" + props.projectSlug, {
        method: "POST",
        body: data,
      })
        .then((response) => {
          return response.json();
        })
        .then((data) => {
          console.log(data);
          if (props.updateGlobalAssets) {
            props.updateGlobalAssets(data);
          }
        });
    }
  }

  function eraseAsset(assetUrl: string, assetName: string) {
    const urlParts = assetUrl.split("/");
    const bucketName = urlParts[3];
    const objectName = urlParts.slice(4).join("/");
    fetch("/api/erase-deck-asset/" + props.projectSlug, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        assetUrl: objectName,
        targetId: props.targetId,
        assetName,
      }),
    }).then((response) => {
      if (response.ok && props.targetId) {
        props.assetDeleted(props.targetId, assetName);
      }
    });
  }

  async function downloadAsset(assetUrl: string, assetName: string) {
    await fetch(assetUrl)
      .then((res) => res.blob())
      .then((blob) => {
        saveAs(blob, assetName);
      });
  }

  async function downloadAllAssets() {
    var zip = new JSZip();
    for (let name of Object.keys(props.assets ?? {}).sort()) {
      let url = props.assets[name].url;
      if (!url) continue;
      let imgName = (url.split("/").pop() ?? "").split("?")[0];
      const imgBlob = await fetch(url).then((res) => res.blob());
      zip.file(imgName, imgBlob);
    }
    let zipName = props.deckName
      ? props.projectSlug + "-" + props.deckName + "-assets.zip"
      : props.projectSlug + "-global-assets.zip";
    zip.generateAsync({ type: "blob" }).then(function (content) {
      saveAs(content, zipName);
    });
  }

  return (
    <div className="assets-manager">
      <form onSubmit={updateAssets}>
        <div>
          <label htmlFor="additional-assets">Upload new assets</label>
          <input type="file" name="additional-assets" multiple></input>
          <input type="hidden" name="targetId" value={props.targetId}></input>
        </div>
        <input type="submit" value="Update"></input>
      </form>
      <div>{assetStatus}</div>
      There are {Object.keys(props.assets ?? {}).length} assets:
      {showCompactViewControl ? (
        <div>
          Compact view:
          <input
            type="checkbox"
            checked={compactAssetsView}
            onChange={(e) => setCompactAssetsView(e.target.checked)}
          ></input>
        </div>
      ) : null}
      <div>
        <button onClick={downloadAllAssets}>Download all assets as ZIP</button>
      </div>
      <div className={"asset-dump" + (compactAssetsView ? " compact" : "")} ref={assetDumpRef}>
        {assetList}
      </div>
    </div>
  );
}
